<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ errors }">
    <form
      class="academic-records-form"
      @submit.prevent="customPasses(onSubmit)">
      <BaseSelectGroupWithProvider
        :value="form.gradeCode"
        name="学年を選択"
        label="学年を選択"
        :options="gradeOptions"
        :rules="`required|grade_code_unique:${existingGradeCodes}`"
        vid="gradeCode"
        select-class="grade-code-select"
        :response-errors="responseErrors"
        @input="onInputGradeCode" />
      <div
        v-for="(semester, semesterIndex) in form.semesters"
        :key="`semester-${semesterIndex}`"
        class="semester-container mb-2 position-relative">
        <LinkButton
          class="position-absolute"
          style="top: 4px; right: 4px;"
          @click="onRemoveSemester(semesterIndex)">
          <RoundedIcon
            color="danger"
            icon="delete" />
        </LinkButton>
        <BaseSelectGroupWithProvider
          v-model="form.semesters[semesterIndex].semester"
          name="学期を選択"
          label="学期を選択"
          :options="semesterOptions"
          :rules="`required|semester_unique:${existingSemesters}`"
          :vid="`semesters[${semesterIndex}].semester`"
          select-class="semester-select" />
        <label class="fs-m font-weight-bold mb-2">成績管理</label>
        <div class="semester-content">
          <div class="d-flex justify-content-between">
            <div
              v-for="(file, fileIndex) in form.semesters[semesterIndex].files"
              :key="`semester-${semesterIndex}-file-${fileIndex}`"
              class="d-flex flex-column align-items-center mb-2"
              style="width: 30%;">
              <label class="py-1 mb-0 fs-s">ファイル添付</label>
              <FileUploaderWithProvider
                :value="form.semesters[semesterIndex].files[fileIndex].file"
                preview-image-class="w-100"
                :vid="`semesters[${semesterIndex}].files[${fileIndex}]`"
                @input="e => onInputFile(semesterIndex, fileIndex, e)" />
            </div>
          </div>

          <div
            v-if="canInputSubjectScores"
            class="mb-2">
            <div
              v-if="isHighSchool"
              class="d-flex justify-content-between pb-2">
              <LinkButton @click="onCopySubjectScores(semesterIndex)">
                <span class="material-icons mr-1">
                  content_copy
                </span>
                前回の科目をコピー
              </LinkButton>
              <LinkButton @click="onClearSubjectScores(semesterIndex)">
                <span class="material-icons mr-1">
                  delete
                </span>
                科目を消す
              </LinkButton>
            </div>
            <b-row
              class="mx-0"
              cols="5">
              <b-col
                v-for="(subjectScore, subjectScoreIndex) in form.semesters[semesterIndex].subjectScores"
                :key="subjectScoreIndex"
                class="mx-0 mb-2 px-1 text-center">
                <BaseTextFieldWithProvider
                  v-if="canInputSubjectTitles"
                  v-model="form.semesters[semesterIndex].subjectScores[subjectScoreIndex].subjectName"
                  :vid="`semesters[${semesterIndex}].subjectScores[${subjectScoreIndex}].subjectName`"
                  placeholder="科目入力"
                  field-class="text-center mb-1 fs-s subject-name-input" />
                <label
                  v-else
                  class="py-2 mb-0 fs-s">{{ subjectScore.subjectName }}</label>
                <BaseTextFieldWithProvider
                  v-model="form.semesters[semesterIndex].subjectScores[subjectScoreIndex].score"
                  :vid="`semesters[${semesterIndex}].subjectScores[${subjectScoreIndex}].score`"
                  placeholder="成績"
                  inputmode="numeric"
                  empty-str-to-null
                  rules="integer|min_value:0|max_value:999"
                  name="成績"
                  field-class="text-center fs-m h-40px"
                  hide-error-message />
              </b-col>
            </b-row>
            <BaseErrorMessage
              v-for="key in Object.keys(errors).filter(key => key.startsWith(`semesters[${semesterIndex}].subjectScores`))"
              :key="key"
              :errors="errors[key]" />
          </div>
          <div class="mb-2">
            <b-row class="table-row mx-0">
              <b-col class="table-cell">
                <label class="py-2 mb-0 fs-s">出席日数</label>
                <BaseTextFieldWithProvider
                  v-model="form.semesters[semesterIndex].attendanceDays"
                  inputmode="numeric"
                  :vid="`semesters[${semesterIndex}].attendanceDays`"
                  field-class="text-center h-40px"
                  name="出席日数" />
              </b-col>
              <b-col class="table-cell">
                <label class="py-2 mb-0 fs-s">欠席日数</label>
                <BaseTextFieldWithProvider
                  v-model="form.semesters[semesterIndex].absenceDays"
                  inputmode="numeric"
                  :vid="`semesters[${semesterIndex}].absenceDays`"
                  field-class="text-center h-40px"
                  name="欠席日数" />
              </b-col>
              <b-col class="table-cell">
                <label class="py-2 mb-0 fs-s">遅刻日数</label>
                <BaseTextFieldWithProvider
                  v-model="form.semesters[semesterIndex].lateDays"
                  inputmode="numeric"
                  :vid="`semesters[${semesterIndex}].lateDays`"
                  field-class="text-center h-40px"
                  name="遅刻日数" />
              </b-col>
              <b-col class="table-cell">
                <label class="py-2 mb-0 fs-s">早退日数</label>
                <BaseTextFieldWithProvider
                  v-model="form.semesters[semesterIndex].earlyLeaveDays"
                  inputmode="numeric"
                  :vid="`semesters[${semesterIndex}].earlyLeaveDays`"
                  field-class="text-center h-40px"
                  name="早退日数" />
              </b-col>
              <b-col class="table-cell">
                <label class="py-2 mb-0 fs-s">忌引日数</label>
                <BaseTextFieldWithProvider
                  v-model="form.semesters[semesterIndex].exclusionDays"
                  inputmode="numeric"
                  :vid="`semesters[${semesterIndex}].exclusionDays`"
                  field-class="text-center h-40px"
                  name="忌引日数" />
              </b-col>
            </b-row>
          </div>
        </div>

        <div
          v-if="canInputTestResults"
          class="mb-2">
          <label class="fs-m font-weight-bold mb-2">テスト結果</label>
          <div
            v-for="(testResult, testResultIndex) in form.semesters[semesterIndex].testResults"
            :key="testResultIndex"
            class="test-result-container position-relative pt-3">
            <LinkButton
              class="position-absolute"
              style="top: 4px; right: 4px;"
              @click="onRemoveTestResult(semesterIndex, testResultIndex)">
              <RoundedIcon
                color="danger"
                icon="delete" />
            </LinkButton>
            <div
              class="d-flex justify-content-start align-items-center"
              style="gap: 8px;">
              <div style="flex: 1;">
                <BaseSelectGroupWithProvider
                  v-model="form.semesters[semesterIndex].testResults[testResultIndex].testName"
                  name="テスト名"
                  label="テスト名を選択"
                  :options="testNamesOptions"
                  :vid="`semesters[${semesterIndex}].testResults[${testResultIndex}].testName`"
                  rules="required" />
              </div>
              <div style="flex: 1;">
                <label class="font-weight-bold fs-m">
                  テスト日付
                </label>
                <BaseDatepickerWithProvider
                  v-model="form.semesters[semesterIndex].testResults[testResultIndex].testDate"
                  name="テスト日付"
                  label-no-date-selected=""
                  datepicker-class="w-100 mb-3 fs-m"
                  :date-format-options="{
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    weekday: 'short'
                  }"
                  rules="required"
                  :vid="`semesters[${semesterIndex}].testResults[${testResultIndex}].testDate`" />
              </div>
            </div>
            <div>
              <div
                v-if="isHighSchool"
                class="d-flex justify-content-between pb-2">
                <LinkButton @click="onCopyTestResultSubjectScores(semesterIndex, testResultIndex)">
                  <span class="material-icons mr-1">
                    content_copy
                  </span>
                  前回の科目をコピー
                </LinkButton>
                <LinkButton @click="onClearTestResultSubjectScores(semesterIndex, testResultIndex)">
                  <span class="material-icons mr-1">
                    delete
                  </span>
                  科目を消す
                </LinkButton>
              </div>
              <b-row
                class="table-row mx-0"
                cols="5">
                <b-col
                  v-for="(subjectScore, subjectScoreIndex) in form.semesters[semesterIndex].testResults[testResultIndex].subjectScores"
                  :key="subjectScoreIndex"
                  class="table-cell">
                  <div class="border-bottom">
                    <BaseTextFieldWithProvider
                      v-if="canInputSubjectTitles"
                      v-model="form.semesters[semesterIndex].testResults[testResultIndex].subjectScores[subjectScoreIndex].subjectName"
                      placeholder="科目入力"
                      field-class="text-center fs-s h-40px"
                      name="科目名"
                      :vid="`semesters[${semesterIndex}].testResults[${testResultIndex}].subjectScores[${subjectScoreIndex}].subjectName`" />
                    <label
                      v-else
                      class="py-2 mb-0 fs-m">{{ subjectScore.subjectName }}</label>
                  </div>
                  <BaseTextFieldWithProvider
                    v-model="form.semesters[semesterIndex].testResults[testResultIndex].subjectScores[subjectScoreIndex].score"
                    placeholder="点数"
                    inputmode="numeric"
                    empty-str-to-null
                    rules="integer"
                    name="点数"
                    :vid="`semesters[${semesterIndex}].testResults[${testResultIndex}].subjectScores[${subjectScoreIndex}].score`"
                    field-class="text-center fs-m h-40px"
                    hide-error-message />
                </b-col>
              </b-row>
              <BaseErrorMessage
                v-for="key in Object.keys(errors).filter(key => key.startsWith(`semesters[${semesterIndex}].testResults[${testResultIndex}]`))"
                :key="key"
                :errors="errors[key]" />
            </div>
          </div>
        </div>
        <LinkButton
          v-if="canInputTestResults"
          class="my-2"
          @click="onAddTestResult(semesterIndex)">
          <RoundedIcon
            class="mr-2"
            color="l-blue"
            icon="add" />
          記録するテスト結果を追加
        </LinkButton>
      </div>
      <LinkButton
        class="my-2"
        @click="onAddSemester">
        <RoundedIcon
          class="mr-2"
          color="l-blue"
          icon="add" />
        記録する学期を追加
      </LinkButton>
      <div
        class="d-flex justify-content-between mt-4"
        style="gap: 16px;">
        <button
          type="button"
          class="btn btn-block btn-cancel"
          @click="onClickCancel">
          キャンセル
        </button>
        <button
          type="submit"
          class="btn btn-block btn-primary mt-0">
          保存
        </button>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import BaseSelectGroupWithProvider from '@/components/parts/BaseSelectGroupWithProvider.vue'
import FileUploaderWithProvider from '@/components/parts/FileUploaderWithProvider.vue'
import BaseTextFieldWithProvider from '@/components/parts/BaseTextFieldWithProvider.vue'
import BaseDatepickerWithProvider from '@/components/parts/BaseDatepickerWithProvider.vue'
import LinkButton from '@/components/parts/LinkButton.vue'
import RoundedIcon from '@/components/parts/RoundedIcon.vue'
import BaseErrorMessage from '@/components/parts/BaseErrorMessage.vue'
import validateMixin from '@/components/mixin/validate'
import cloneDeep from 'lodash/cloneDeep'

import {
  academicRecordGradeCodeOptions,
  semesterOptions,
  scoreOptions,
  isJuniorHighSchool,
  juniorHighSchoolSubjectTitles,
  canInputSubjectScores,
  canInputSubjectTitles,
  testNamesOptions,
  canInputTestResults,
  isHighSchool,
  gradeCodeToGrade,
  semesterToText,
  testNameToText,
  semesterSortNumByValue
} from '@/helper/profile'
import { fileToBase64 } from '@/utils/file'
import { trimDecimalZero } from '@/helper/number'

export default {
  components: {
    BaseSelectGroupWithProvider,
    FileUploaderWithProvider,
    BaseTextFieldWithProvider,
    BaseDatepickerWithProvider,
    LinkButton,
    RoundedIcon,
    BaseErrorMessage
  },
  mixins: [validateMixin],
  props: {
    academicRecord: {
      type: Object,
      default: null
    },
    // 既存の学年を取得する
    // 既存の学年を選択した時にエラーを表示するため
    existingAcademicRecords: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      form: {
        gradeCode: null,
        semesters: [
          {
            semester: null,
            files: [
              {
                file: undefined,
                displayOrder: 0
              },
              {
                file: undefined,
                displayOrder: 1
              },
              {
                file: undefined,
                displayOrder: 2
              }
            ],
            attendanceDays: null,
            absenceDays: null,
            lateDays: null,
            earlyLeaveDays: null,
            exclusionDays: null,
            subjectScores: [],
            testResults: []
          },
        ],
      },
      responseErrors: {} // サーバーエラーを格納
    }
  },
  computed: {
    isEdit() {
      return this.academicRecord !== null
    },
    gradeOptions() {
      return academicRecordGradeCodeOptions
    },
    existingGradeCodes() {
      // 編集の場合はその学年は選べるようにしておく
      if (this.isEdit) {
        return this.existingAcademicRecords.map(academicRecord => academicRecord.gradeCode).filter(gradeCode => gradeCode !== this.academicRecord.gradeCode)
      }
      return this.existingAcademicRecords.map(academicRecord => academicRecord.gradeCode)
    },
    existingSemesters() {
      return this.form.semesters.map(semester => semester.semester)
    },
    semesterOptions() {
      return semesterOptions
    },
    scoreOptions() {
      return scoreOptions
    },
    testNamesOptions() {
      return testNamesOptions
    },
    canInputSubjectScores() {
      return canInputSubjectScores(this.form.gradeCode)
    },
    canInputSubjectTitles() {
      return canInputSubjectTitles(this.form.gradeCode)
    },
    canInputTestResults() {
      return canInputTestResults(this.form.gradeCode)
    },
    isHighSchool() {
      return isHighSchool(this.form.gradeCode)
    },
  },
  created() {
    this.setAcademicRecord()
  },
  methods: {
    setAcademicRecord() {
      if (this.academicRecord) {
        const academicRecord = cloneDeep(this.academicRecord)
        this.form.gradeCode = academicRecord.gradeCode
        academicRecord.semesters.forEach(semester => {
          const fileByDisplayOrder = semester.files.reduce((acc, file) => {
            acc[file.displayOrder] = file
            return acc
          }, {})
          semester.files = Array.from({ length: 3 }).map((_, index) => {
            const existingFile = fileByDisplayOrder[index]
            if (existingFile) {
              return existingFile
            }
            return { file: undefined, displayOrder: index }
          })
          semester.subjectScores = this.getDefaultSubjectScores().map((defaultSubjectScore, index) => {
            const existingSubjectScore = semester.subjectScores[index]
            if (existingSubjectScore) {
              return {
                ...existingSubjectScore,
                subjectName: existingSubjectScore.subjectName,
                score: existingSubjectScore.score,
                displayOrder: index
              }
            }
            return {
              subjectName: defaultSubjectScore.subjectName,
              score: null,
              displayOrder: index
            }
          })
          semester.testResults.forEach(testResult => {
            testResult.subjectScores = this.getDefaultSubjectScores().map((defaultSubjectScore, index) => {
              const existingSubjectScore = testResult.subjectScores[index]
              if (existingSubjectScore) {
                return {
                  ...existingSubjectScore,
                  subjectName: existingSubjectScore.subjectName,
                  score: trimDecimalZero(existingSubjectScore.score),
                  displayOrder: index
                }
              }
              return {
                subjectName: defaultSubjectScore.subjectName,
                score: null,
                displayOrder: index
              }
            })
          })
        })
        this.form = academicRecord
      }
    },
    async onSubmit() {
      const payload = await this.buildFormPayload()
      this.$emit('submit', payload)
    },
    async buildFormPayload() {
      const form = cloneDeep(this.form)
      const semesters = await Promise.all(form.semesters.map(async (semester) => {
        const files = await this.buildFilesPayload(semester.files)
        return {
          ...semester,
          files,
          subjectScores: this.canInputSubjectScores
            ? semester.subjectScores.filter(subjectScore => subjectScore.subjectName !== null)
            : undefined,
          testResults: this.canInputTestResults
            ? semester.testResults.reduce((acc, testResult) => {
              if (testResult.testName !== null) {
                acc.push({
                  ...testResult,
                  subjectScores: testResult.subjectScores.filter(subjectScore => subjectScore.subjectName !== null)
                })
              }
              return acc
            }, [])
            : undefined
        }
      }))
      return { ...form, semesters }
    },
    async buildFilesPayload(fileEntries) {
      const usableEntries = fileEntries.filter(entry => entry && (entry.id || entry.file))
      return Promise.all(usableEntries.map(entry => this.convertFileEntry(entry)))
    },
    async convertFileEntry(entry) {
      if (entry.id) {
        return { id: entry.id, displayOrder: entry.displayOrder }
      }
      const base64 = await fileToBase64(entry.file)
      return { file: base64, filename: entry.file.name, displayOrder: entry.displayOrder }
    },
    getDefaultFiles() {
      return Array.from({ length: 3 }).map((_, index) => ({
        file: undefined,
        displayOrder: index
      }))
    },
    getDefaultSemester() {
      return {
        semester: null,
        files: this.getDefaultFiles(),
        subjectScores: this.getDefaultSubjectScores(),
        testResults: [this.getDefaultTestResults()]
      }
    },
    // 中学生の場合は固定のsubjectNameにする
    getDefaultSubjectScores() {
      if (isJuniorHighSchool(this.form.gradeCode)) {
        return juniorHighSchoolSubjectTitles.map((title, index) => ({
          subjectName: title,
          score: null,
          displayOrder: index
        }))
      }
      return Array.from({ length: 15 }).map((_, index) => ({
        subjectName: null,
        score: null,
        displayOrder: index
      }))
    },
    getDefaultTestResults() {
      if (isJuniorHighSchool(this.form.gradeCode)) {
        return {
          testName: null,
          testDate: null,
          subjectScores: this.getDefaultSubjectScores()
        }
      }
      return {
        testName: null,
        testDate: null,
        subjectScores: this.getDefaultSubjectScores()
      }
    },
    onInputGradeCode(value) {
      this.form.gradeCode = value
      this.form.semesters.forEach(semester => {
        semester.subjectScores = this.getDefaultSubjectScores()
        semester.testResults = []
      })

      this.$refs.observer.reset()
      
      // 重複チェック
      if (this.existingGradeCodes.includes(value)) {
        this.$refs.observer.setErrors({
          gradeCode: ['既に登録されている学年です']
        })
      }
    },
    onInputTestName(semesterIndex, testResultIndex, value) {
      this.form.semesters[semesterIndex].testResults[testResultIndex].testName = value
    },
    onInputFile(semesterIndex, fileIndex, value) {
      this.form.semesters[semesterIndex].files[fileIndex] = {
        file: value,
        displayOrder: fileIndex
      }
    },
    onAddSemester() {
      this.form.semesters.push(this.getDefaultSemester())
    },
    onAddTestResult(semesterIndex) {
      this.form.semesters[semesterIndex].testResults.push(this.getDefaultTestResults())
    },
    onRemoveSemester(semesterIndex) {
      if (!window.confirm('この学期を削除してもよろしいですか？削除するとこの学期に入力した記録もすべて削除されます。')) return
      this.form.semesters.splice(semesterIndex, 1)
    },
    onRemoveTestResult(semesterIndex, testResultIndex) {
      this.form.semesters[semesterIndex].testResults.splice(testResultIndex, 1)
    },
    onClickCancel() {
      this.$router.back()
    },
    async onCopyTestResultSubjectScores(semesterIndex, testResultIndex) {
      const testDate = this.form.semesters[semesterIndex].testResults[testResultIndex].testDate
      if (!testDate) {
        this.$store.commit('notify/set', {message: 'テスト日を入力してください'})
        return
      }
      // 同じ学年の最新のテスト結果を取得し、コピーする
      const { latestTestResult: latestSameGradeTestResult, latestAcademicRecord: latestSameGradeAcademicRecord, latestSemester: latestSameGradeSemester } = this.getLatestTestResultWithAcademicRecord([this.form], testDate)
      if (latestSameGradeTestResult) {
        const confirmed = window.confirm(`${gradeCodeToGrade(latestSameGradeAcademicRecord.gradeCode)}${semesterToText(latestSameGradeSemester.semester)}の${testNameToText(latestSameGradeTestResult.testName)}のテスト科目名をコピーしますか？`)
        if (!confirmed) {
          return
        }
        this.form.semesters[semesterIndex].testResults[testResultIndex].subjectScores = latestSameGradeTestResult.subjectScores.map((subjectScore, index) => ({ ...subjectScore, score: null, displayOrder: index }))
        return
      }

      // 過去の学年データを取得し、コピーをする
      const previousAcademicRecords = await this.fetchHighSchoolAcademicRecordsDetails(this.form.gradeCode)
      const previousSemesters = previousAcademicRecords.flatMap(academicRecord => academicRecord.semesters)
      if (previousSemesters.length === 0) {
        this.$store.commit('notify/set', {message: '高校の過去のテスト結果がありません'})
        return
      }
      const { latestTestResult, latestAcademicRecord, latestSemester } = this.getLatestTestResultWithAcademicRecord(previousAcademicRecords, testDate)
      if (latestTestResult) {
        const confirmed = window.confirm(`${gradeCodeToGrade(latestAcademicRecord.gradeCode)}${semesterToText(latestSemester.semester)}の${testNameToText(latestTestResult.testName)}のテスト科目名をコピーしますか？`)
        if (!confirmed) {
          return
        }
        const copiedSubjectScores = this.copySubjectScores(latestTestResult.subjectScores, this.form.semesters[semesterIndex].testResults[testResultIndex].subjectScores)
        this.form.semesters[semesterIndex].testResults[testResultIndex].subjectScores = copiedSubjectScores
        return
      }
      this.$store.commit('notify/set', {message: '高校の過去のテスト結果がありません'})
    },
    // 引数の学期の中からテスト日付に一番近いテスト結果を取得し、その学年と学期も取得する
    getLatestTestResultWithAcademicRecord(academicRecords, testDate) {
      let latestTestResult = null
      let latestAcademicRecord = null
      let latestSemester = null
      for (const academicRecord of academicRecords) {
        for (const semester of academicRecord.semesters) {
          for (const testResult of semester.testResults) {
            if (testResult.testDate >= testDate) {
              continue
            } else if (!latestTestResult) {
              latestTestResult = testResult
              latestAcademicRecord = academicRecord
              latestSemester = semester
            } else if (testResult.testDate && testResult.testDate < testDate && testResult.testDate > latestTestResult.testDate) {
              latestTestResult = testResult
              latestAcademicRecord = academicRecord
              latestSemester = semester
            }
          }
        }
      }
      return { latestTestResult, latestAcademicRecord, latestSemester }
    },
    // 学年の成績詳細を取得する
    fetchAcademicRecordDetail(academicRecordId) {
      return this.$axios.get(`/profiles/academic-semesters/${academicRecordId}/`).then(res => res.data)
    },
    // 現在の学年以前の成績詳細を取得する
    fetchHighSchoolAcademicRecordsDetails(currentGradeCode) {
      const previousGradeCodes = [10, 11].filter(gradeCode => gradeCode < currentGradeCode)
      const previousGradeCodesAcademicRecords = this.existingAcademicRecords.filter(academicRecord => previousGradeCodes.includes(academicRecord.gradeCode))
      if (previousGradeCodesAcademicRecords.length === 0) {
        return Promise.resolve([])
      }
      const previousGradeCodesAcademicRecordIds = previousGradeCodesAcademicRecords.map(academicRecord => academicRecord.id)
      return Promise.all(previousGradeCodesAcademicRecordIds.map(academicRecordId => this.fetchAcademicRecordDetail(academicRecordId)))
    },
    copySubjectScores(sourceSubjectScores, targetSubjectScores) {
      return targetSubjectScores.map((targetSubjectScore, index) => {
        const sourceSubjectScore = sourceSubjectScores[index]
        return { subjectName: sourceSubjectScore ? sourceSubjectScore.subjectName : null, score: targetSubjectScore.score, displayOrder: index }
      })
    },
    async onCopySubjectScores(semesterIndex) {
      const currentSemester = this.form.semesters[semesterIndex]
      if (currentSemester.semester === null) {
        this.$store.commit('notify/set', {message: '学期を選択してください'})
        return
      }
      const { latestSubjectScores: latestSameGradeSubjectScores, latestAcademicRecord: latestSameGradeAcademicRecord, latestSemester: latestSameGradeSemester } = this.getLatestSubjectScoresWithAcademicRecord([this.form], this.form, currentSemester)
      if (latestSameGradeSubjectScores) {
        const confirmed = window.confirm(`${gradeCodeToGrade(latestSameGradeAcademicRecord.gradeCode)}${semesterToText(latestSameGradeSemester.semester)}の科目名をコピーしますか？`)
        if (!confirmed) {
          return
        }
        const copiedSubjectScores = this.copySubjectScores(latestSameGradeSubjectScores, this.form.semesters[semesterIndex].subjectScores)
        this.form.semesters[semesterIndex].subjectScores = copiedSubjectScores
        return
      }

      const previousAcademicRecords = await this.fetchHighSchoolAcademicRecordsDetails(this.form.gradeCode)
      if (previousAcademicRecords.length === 0) {
        this.$store.commit('notify/set', {message: '高校の過去の学年の成績がありません'})
        return
      }
      const { latestSubjectScores, latestAcademicRecord, latestSemester } = this.getLatestSubjectScoresWithAcademicRecord(previousAcademicRecords, this.form, currentSemester)
      if (latestSubjectScores) {
        const confirmed = window.confirm(`${gradeCodeToGrade(latestAcademicRecord.gradeCode)}${semesterToText(latestSemester.semester)}の科目名をコピーしますか？`)
        if (!confirmed) {
          return
        }
        const copiedSubjectScores = this.copySubjectScores(latestSubjectScores, this.form.semesters[semesterIndex].subjectScores)
        this.form.semesters[semesterIndex].subjectScores = copiedSubjectScores
        return
      }
      this.$store.commit('notify/set', {message: '高校の過去の学年の成績がありません'})
    },
    getLatestSubjectScoresWithAcademicRecord(academicRecords, currentAcademicRecord, currentSemester) {
      let latestSubjectScores = null
      let latestAcademicRecord = null
      let latestSemester = null
      for (const academicRecord of academicRecords) {
        for (const semester of academicRecord.semesters) {
          // 学年が指定したものより未来の場合は除く
          if (academicRecord.gradeCode > currentAcademicRecord.gradeCode) {
            continue
          }
          const currentSemesterSortNum = semesterSortNumByValue[currentSemester.semester]
          const semesterSortNum = semesterSortNumByValue[semester.semester]
          // 学年と学期が指定したものより未来の場合は除く
          if (academicRecord.gradeCode === currentAcademicRecord.gradeCode && currentSemesterSortNum <= semesterSortNum) {
            continue
          }
          // latestSemesterに値がない場合は入れる
          if (!latestSemester) {
            latestSubjectScores = semester.subjectScores
            latestAcademicRecord = academicRecord
            latestSemester = semester
            continue
          }
          // latestAcademicRecordとacademicRecordを比較し、latestAcademicRecordの方が新しい場合はスキップ
          if (academicRecord.gradeCode < latestAcademicRecord.gradeCode) {
            continue
          }
          // latestAcademicRecordとacademicRecordを比較し、academicRecordの方が新しい場合は入れ替える
          if (academicRecord.gradeCode > latestAcademicRecord.gradeCode && academicRecord.semesters) {
            latestSubjectScores = semester.subjectScores
            latestAcademicRecord = academicRecord
            latestSemester = semester
            continue
          }
          // latestAcademicRecordとacademicRecordが同じ場合は学期が新しければ入れ替える
          const latestSemesterSortNum = semesterSortNumByValue[latestSemester.semester]
          if (academicRecord.gradeCode === latestAcademicRecord.gradeCode && semesterSortNum > latestSemesterSortNum) {
            latestSubjectScores = semester.subjectScores
            latestAcademicRecord = academicRecord
            latestSemester = semester
          }
        }
      }
      return { latestSubjectScores, latestAcademicRecord, latestSemester }
    },
    onClearSubjectScores(semesterIndex) {
      if (!window.confirm('科目を削除しますか？')) return
      const newSubjectScores = this.form.semesters[semesterIndex].subjectScores.map((subjectScore, index) => ({ subjectName: null, score: subjectScore.score, displayOrder: index }))
      this.form.semesters[semesterIndex].subjectScores = newSubjectScores
    },
    onClearTestResultSubjectScores(semesterIndex, testResultIndex) {
      if (!window.confirm('科目を削除しますか？')) return
      const newSubjectScores = this.form.semesters[semesterIndex].testResults[testResultIndex].subjectScores.map((subjectScore, index) => ({ subjectName: null, score: subjectScore.score, displayOrder: index }))
      this.form.semesters[semesterIndex].testResults[testResultIndex].subjectScores = newSubjectScores
    }
  },
}
</script>

<style lang="scss" scoped>

.semester-select-group-container {
  width: 120px;
}

.semester-container {
  border: 1px solid $grey11;
  padding: 8px;
}

.semester-content {
  border: 1px solid $grey11;
  padding: 8px;
  margin-bottom: 8px;
}

.test-result-container {
  border: 1px solid $grey11;
  padding: 8px;
  margin-bottom: 8px;
}

.table-row {
  display: flex;
  align-items: flex-start;
}

.table-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;

  // cellに枠線をつける
  &:after {
    position: absolute;
    box-sizing: border-box;
    top: -0.5px;
    left: -0.5px;
    content: '';
    width: calc(100% + 1px);
    height: calc(100% + 1px);
    border: 1px solid $grey11;
    pointer-events: none;
  }
}
</style>

<style lang="scss">
.h-40px {
  height: 40px !important;
}

.subject-name-input {
  height: 40px !important;
}

.grade-code-select {
  width: 160px !important;
}

.semester-select {
  width: 160px !important;
}
</style>